import { RegistrationCommitteeItem } from "registration-committee-items";
import { Decision, DecisionTypeCode } from ".";
import { PracticeReview } from "practice-reviews";
import { DocType } from "common/DocType";

export function getManagerDecisionInaccessibleReason(practiceReview: PracticeReview) {
  const reviewerDecision = practiceReview.decisions.filter((d) => d.decisionType.typeCode === DecisionTypeCode.Reviewer)?.[0];

  if (practiceReview.isPprpProgramReview) {
    return "There is no manager decision on PPRP Program Reviews.";
  }

  if (!reviewerDecision?.signedOff) {
    return "The reviewer decision is not signed off.";
  }

  if (!practiceReview.isReturnedComplete) {
    return "The PR has not been returned complete.";
  }

  const managerDecision = practiceReview.decisions.filter((d) => d.decisionType.typeCode === DecisionTypeCode.Manager)?.[0];
  if (!managerDecision) {
    return "The manager decision has not been created yet.";
  }

  return null;
}

export function getDirectorDecisionInaccessibleReason(practiceReview: PracticeReview) {
  const managerDecision = practiceReview.decisions.filter((d) => d.decisionType.typeCode === DecisionTypeCode.Manager)?.[0];
  if (!practiceReview.isPprpProgramReview && !managerDecision?.signedOff) {
    return "The manager decision is not signed off.";
  }

  const directorDecision = practiceReview.decisions.filter((d) => d.decisionType.typeCode === DecisionTypeCode.Director)?.[0];
  if (!directorDecision) {
    return "The director decision has not been created yet.";
  }

  return null;
}

export function getCommitteeDecisionInaccessibleReason(practiceReview: PracticeReview) {
  const directorDecision = practiceReview.decisions.filter((d) => d.decisionType.typeCode === DecisionTypeCode.Director)?.[0];
  if (!directorDecision?.signedOff) {
    return "The director decision is not signed off.";
  }

  const committeeDecision = practiceReview.decisions.filter((d) => d.decisionType.typeCode === DecisionTypeCode.Committee)?.[0];
  if (!committeeDecision) {
    return "The committee decision has not been created yet.";
  }

  return null;
}

export function getMonitorDirectedPDInaccessibleReason(practiceReview?: PracticeReview) {
  const committeeDecision = practiceReview?.decisions.filter((d) => d.decisionType.typeCode === DecisionTypeCode.Committee)?.[0];
  const hasDirected = committeeDecision?.assignedPdCourses.some((course) => course.isDirected);

  if (!committeeDecision?.signedOff) {
    return "The committee decision is not signed off.";
  }
  if (!hasDirected) {
    return "There are no directed PD courses.";
  }

  return null;
}

export function shouldConfirmDecisionChange(
  decision: Decision | null,
  allDecisions: Decision[],
  currentIsComply: boolean | null,
  isPprpProgramReview = false
) {
  if (!decision || currentIsComply === null || isPprpProgramReview) return false;

  if (decision.decisionType.typeCode === DecisionTypeCode.Manager || decision.decisionType.typeCode === DecisionTypeCode.Director) {
    const previousDecisionCode =
      decision.decisionType.typeCode === DecisionTypeCode.Manager ? DecisionTypeCode.Reviewer : DecisionTypeCode.Manager;
    const previousDecision = allDecisions.find((d) => d.decisionType.typeCode === previousDecisionCode)!;
    return !decision.discussChangesCompleted && !decision.hasOutstandingDiscussChanges && previousDecision.isComply !== currentIsComply;
  } else return false;
}

export function shouldCancelDecisionChange(decision: Decision | null, allDecisions: Decision[], currentIsComply: boolean | null) {
  if (!decision || currentIsComply === null) return false;

  if (decision.decisionType.typeCode === DecisionTypeCode.Manager || decision.decisionType.typeCode === DecisionTypeCode.Director) {
    const previousDecisionCode =
      decision.decisionType.typeCode === DecisionTypeCode.Manager ? DecisionTypeCode.Reviewer : DecisionTypeCode.Manager;
    const previousDecision = allDecisions.find((d) => d.decisionType.typeCode === previousDecisionCode)!;
    return (
      decision.hasOutstandingDiscussChanges &&
      previousDecision.isComply !== decision.isComply &&
      previousDecision.isComply === currentIsComply
    );
  } else return false;
}

export function isReviewDecisionIncomplete(practiceReview: PracticeReview) {
  if (practiceReview.tabledToNext) {
    // CSN 2022-04-28: "Don't count tabled PR decisions as "incomplete" on Enter Committee Decisions screen (#59522)."
    // I guess this works because when this property is fetched from graphQL it requires a meetingId to be with respect
    // to... but it doesn't FEEL correct to be on the PracticeReview model, and it seems to me like this could be wrong,
    // especially once you consider the implicit cache and reuse of things by apollo...
    // But what do I know; it's been like this for more than 2 years, and all I'm doing is moving this logic to somewhere
    // it can be unit tested, so if there's never been a bug with it then I guess it's fine?
    return false;
  }

  const decision = practiceReview.mostAuthoritativeDecision;
  // - the most authoritative decision should be a committee decision (if not, we haven't even done all the decisions yet)
  if (!decision || decision.decisionType.typeCode !== DecisionTypeCode.Committee) return true;

  return (
    // - if it's non-comply, it must have a follow-up date, unless it's a PPRP program review
    (!practiceReview.isPprpProgramReview && decision.isComply === false && decision.followUpDate === null) ||
    // - if it has directed PD courses, they must have a due date
    (decision.assignedPdCourses && decision.assignedPdCourses.some((c) => c.isDirected) && !decision.directedPdDueDate)
  );
}

export function isRegComItemDecisionIncomplete(item: RegistrationCommitteeItem) {
  const decision = item.decision;

  // there has to be a decision, obviously
  if (!decision) return true;

  return (
    // - have to have picked one of Approved, Deferred or Refused or Tabled
    (!decision.isApproved && !decision.isDeferred && !decision.isRefused && !decision.isTabled) ||
    // - if monitoring is required, must have either a deadline date OR supplied the count
    (decision.monitoringRequired && !decision.monitoringSingleHardDeadlineDate && !decision.monitoringCount)
  );
}

function regComItemIsLinkedToPprpProgramReview(item: RegistrationCommitteeItem) {
  // this is out of fear not being sure if everything has always been loaded by graphql when I want to know this
  return item.pprpProgramReviewId || item.pprpProgramReview || item.itemType?.isPprpProgramReviewType;
}

function regComItemIsReadyToSendForFinalReview(item: RegistrationCommitteeItem) {
  // can't send it for review if it's already sent for review
  if (item.isFinalReportSentForReview) {
    return false;
  }

  // if it isn't marked as ready for review, then it can't be sent for review
  if (!item.isFinalReportReadyForReview) {
    return false;
  }

  // if it has a PPRP Program Review, then that review must also be ready for review
  // (unless it's tabled, in which case it doesn't matter)
  return (
    !regComItemIsLinkedToPprpProgramReview(item) || item.decision?.isTabled || item.pprpProgramReview?.isFinalReportReviewedByCoordinator
  );
}

export const reasonsReleaseDecisionToFinalReviewIsDisabled = {
  meetingQueryStillLoading: "Not all of the data for the meeting has finished loading.",
  regCom: {
    allItemsAlreadyReleased: "All items are already released for final review.",
    noItemsReady: "No items are marked ready for release to final review.",
    onlyReadyArePartialPprp: "The only items marked ready are connected to PPRP reviews that are not marked ready."
  },
  prc: {
    notAllPrsReady: "All PRs must be marked ready for release to final review."
  }
};

export function getReasonReleaseDecisionToFinalReviewIsDisabled(
  meetingQueryLoading: boolean,
  meetingPrs: PracticeReview[],
  tabledPrs: PracticeReview[],
  isRegistrationCommittee: boolean | undefined,
  itemsForSelectedMeeting: RegistrationCommitteeItem[]
) {
  // obviously can't release if we haven't even loaded to know if we can release
  if (meetingQueryLoading) {
    return reasonsReleaseDecisionToFinalReviewIsDisabled.meetingQueryStillLoading;
  }

  // Registration Committee allows individual releases now so the rules are different:
  if (isRegistrationCommittee) {
    // if all the items are already released, there's nothing to do
    if (itemsForSelectedMeeting.every((item) => item.isFinalReportSentForReview)) {
      return reasonsReleaseDecisionToFinalReviewIsDisabled.regCom.allItemsAlreadyReleased;
    }

    // if there aren't any items ready to be sent for final review, there's nothing to do
    if (!itemsForSelectedMeeting.some((item) => regComItemIsReadyToSendForFinalReview(item))) {
      // give a more specific message if this is because of RCIs linked to unready PPRP PRs
      if (itemsForSelectedMeeting.some((item) => item.isFinalReportReadyForReview && !item.isFinalReportSentForReview)) {
        return reasonsReleaseDecisionToFinalReviewIsDisabled.regCom.onlyReadyArePartialPprp;
      }
      return reasonsReleaseDecisionToFinalReviewIsDisabled.regCom.noItemsReady;
    }

    return "";
  }

  // PRC meetings on the other hand still do all the PRs at once, so wait until they're all ready
  if (
    meetingPrs?.some((pr) => !pr.isFinalReportReviewedByCoordinator) ||
    tabledPrs?.some((pr) => !pr.tabledToNext?.isTabledLetterReviewedByCoordinator)
  ) {
    return reasonsReleaseDecisionToFinalReviewIsDisabled.prc.notAllPrsReady;
  }

  return "";
}
